import type { Node } from '@xyflow/react';

import {
  CANVAS_CONNECT_BUTTON_SIZE,
  CANVAS_CONNECT_DEFAULT_EDGE_GAP,
} from '@/lib/canvas/canvas-connect-handle-hotzone';

export const CANVAS_CUSTOM_GROUP_NODE_TYPE = 'canvasCustomGroup' as const;

export const CANVAS_CUSTOM_GROUP_NODE_CLASSNAME = 'canvas-custom-group-node';

export const CANVAS_CUSTOM_GROUP_ID_PREFIX = 'canvas-custom-group:';

export const CANVAS_CUSTOM_GROUP_PADDING = 28;

/** 行距：组内纵向紧凑。 */
export const CANVAS_CUSTOM_GROUP_GAP = 24;

/**
 * 列距：须让出左右连线圆钮（32）+ 距边 12，避免被邻列卡片盖住。
 * 只加把手 z-index 无效：组员同在 elevated band，后绘制的右列仍会压住左列溢出的把手。
 */
export const CANVAS_CUSTOM_GROUP_COLUMN_GAP =
  CANVAS_CONNECT_BUTTON_SIZE + CANVAS_CONNECT_DEFAULT_EDGE_GAP * 2;

export const CANVAS_CUSTOM_GROUP_DEFAULT_COLUMNS = 4;

export const CANVAS_CUSTOM_GROUP_MIN_COLUMNS = 2;

export const CANVAS_CUSTOM_GROUP_MAX_COLUMNS = 10;

/** 未抬升时组框须低于资产标签图（z≈3）等业务卡；历史组那档 100 会把组员盖住。 */
export const CANVAS_CUSTOM_GROUP_FRAME_Z_INDEX = 0;

export type CanvasCustomGroupLayoutMode = 'grid' | 'horizontal' | 'vertical';

export type CanvasCustomGroupLayoutModeValue = 1 | 2 | 3;

export interface CanvasCustomGroupMemberPosition {
  x: number;
  y: number;
  zIndex?: number;
}

export interface CanvasCustomGroupState {
  groupId: string;
  layoutMode: CanvasCustomGroupLayoutMode;
  gridColumns: number;
  memberKeys: string[];
  memberPositions: Record<string, CanvasCustomGroupMemberPosition>;
}

export interface CanvasCustomGroupNodeData {
  groupId: string;
  memberCount: number;
  title: string;
  layoutMode: CanvasCustomGroupLayoutMode;
  gridColumns: number;
  frameWidth?: number;
  frameHeight?: number;
  [key: string]: unknown;
}

export type CanvasCustomGroupFlowNode = Node<
  CanvasCustomGroupNodeData,
  typeof CANVAS_CUSTOM_GROUP_NODE_TYPE
>;

export interface CanvasCustomGroupNodeSize {
  width: number;
  height: number;
}

export type CanvasCustomGroupSizeFallback =
  | CanvasCustomGroupNodeSize
  | ((node: CanvasCustomGroupNodeLike) => CanvasCustomGroupNodeSize);

export interface CanvasCustomGroupRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CanvasCustomGroupFrameRect extends CanvasCustomGroupRect {
  groupId: string;
}

export interface CanvasCustomGroupLayoutMember {
  layoutKey: string;
  nodeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex?: number;
}

export interface CanvasCustomGroupEditSlot {
  layoutKey: string;
  nodeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CanvasCustomGroupInsertLine {
  orientation: 'vertical' | 'horizontal';
  x: number;
  y: number;
  length: number;
}

export type CanvasCustomGroupEditDrop =
  | { kind: 'remove' }
  | { kind: 'swap'; targetLayoutKey: string; targetRect: CanvasCustomGroupRect }
  | { kind: 'insert'; index: number; line: CanvasCustomGroupInsertLine }
  | { kind: 'noop' };

export type CanvasCustomGroupSelectionKind = 'pre-group' | 'grouped' | 'disabled';

export interface CanvasCustomGroupSelectionAnalysis {
  kind: CanvasCustomGroupSelectionKind;
  groupableNodeIds: string[];
  groupableLayoutKeys: string[];
  group: CanvasCustomGroupState | null;
  disabledReason: string | null;
}

export interface CanvasCustomGroupNodeLike {
  id: string;
  type?: string | null;
  position: { x: number; y: number };
  width?: number | null;
  height?: number | null;
  measured?: { width?: number; height?: number };
  style?: { width?: number | string; height?: number | string };
  data?: unknown;
  zIndex?: number;
  selected?: boolean;
}

export function generateCanvasCustomGroupId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const now = BigInt(Date.now());
  bytes[0] = Number((now >> 40n) & 0xffn);
  bytes[1] = Number((now >> 32n) & 0xffn);
  bytes[2] = Number((now >> 24n) & 0xffn);
  bytes[3] = Number((now >> 16n) & 0xffn);
  bytes[4] = Number((now >> 8n) & 0xffn);
  bytes[5] = Number(now & 0xffn);
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getCanvasCustomGroupFrameNodeId(groupId: string): string {
  return `${CANVAS_CUSTOM_GROUP_ID_PREFIX}${groupId}`;
}

export function parseCanvasCustomGroupIdFromNodeId(nodeId: string): string | null {
  if (!nodeId.startsWith(CANVAS_CUSTOM_GROUP_ID_PREFIX)) return null;
  const groupId = nodeId.slice(CANVAS_CUSTOM_GROUP_ID_PREFIX.length);
  return groupId.length > 0 ? groupId : null;
}

export function isCanvasCustomGroupFrameNode(
  node: Pick<CanvasCustomGroupNodeLike, 'type' | 'id'>
): boolean {
  return (
    node.type === CANVAS_CUSTOM_GROUP_NODE_TYPE ||
    Boolean(parseCanvasCustomGroupIdFromNodeId(node.id))
  );
}

export function formatCanvasCustomGroupTitle(memberCount: number): string {
  return `分组 ${memberCount}个节点`;
}

export function canvasCustomGroupLayoutModeToValue(
  mode: CanvasCustomGroupLayoutMode
): CanvasCustomGroupLayoutModeValue {
  if (mode === 'horizontal') return 2;
  if (mode === 'vertical') return 3;
  return 1;
}

export function canvasCustomGroupLayoutModeFromValue(
  value: number | null | undefined
): CanvasCustomGroupLayoutMode {
  if (value === 2) return 'horizontal';
  if (value === 3) return 'vertical';
  return 'grid';
}

export function clampCanvasCustomGroupGridColumns(value: number): number {
  if (!Number.isFinite(value)) return CANVAS_CUSTOM_GROUP_DEFAULT_COLUMNS;
  return Math.min(
    CANVAS_CUSTOM_GROUP_MAX_COLUMNS,
    Math.max(CANVAS_CUSTOM_GROUP_MIN_COLUMNS, Math.round(value))
  );
}
